import * as React from 'react';

import { Activity, Topic, } from './interfaces';
import { AVERAGE_FONT_WIDTH, TARGET_GRADE_RANGE_BANDS, floorBand, stringifyLowerLevelTopics, } from './shared';

import useWindowSize from '../../../../../Shared/hooks/useWindowSize';
import { Checkbox, ESCAPE, safeParseHtml, safeStripHtml, } from '../../../../../Shared/index';
import { ImageTagForClassification } from '../../../../../Shared';

const expandSrc = `${window.postbuildEnv.CDN_URL}/images/shared/expand.svg`
const conceptSrc = `${window.postbuildEnv.CDN_URL}/images/icons/description-concept.svg`
const ccssSrc = `${window.postbuildEnv.CDN_URL}/images/icons/description-ccss.svg`
const previewSrc = `${window.postbuildEnv.CDN_URL}/images/icons/preview.svg`
const bookmarkSrc = `${window.postbuildEnv.CDN_URL}/images/icons/icons-bookmark.svg`
const outlinedBookmarkSrc = `${window.postbuildEnv.CDN_URL}/images/icons/icons-bookmark-outline.svg`
const removeSrc = `${window.postbuildEnv.CDN_URL}/images/icons/remove-in-circle.svg`

const IMAGE_WIDTH = 18
const MARGIN = 16

interface ActivityRowCheckboxProps {
  activity: Activity,
  isSelected: boolean,
  toggleActivitySelection: (activity: Activity, isSelected: boolean) => void
}

interface ActivityRowProps {
  activity: Activity,
  isSelected: boolean,
  isFirst: boolean,
  toggleActivitySelection: (activity: Activity, isSelected: boolean) => void,
  showCheckbox?: boolean,
  showRemoveButton?: boolean,
  setShowSnackbar?: (show: boolean) => void,
  saveActivity: (activityId: number) => void,
  unsaveActivity: (activityId: number) => void,
  savedActivityIds: number[],
  allTopics: Topic[]
}

// the following method is a pretty hacky solution for helping to determine whether or not to show a truncated string and tooltip or the whole topic string in the <TopicSection />
// we can't rely on pure CSS for this because the max width is so dependent on the presence and length of other elements in the row
const calculateMaxAllowedLengthForTopicSection = ({
  activity_classification,
}): number => {
  let maxAllowedLength = 0
  const container = document.getElementsByClassName('activity-table-container')[0]
  if (container) {
    maxAllowedLength = container.offsetWidth - 128 // 128 is the amount of padding in total that makes up the difference between the width of the container and the width of the second line
    maxAllowedLength -= activity_classification && activity_classification.alias ? (activity_classification.alias.length * AVERAGE_FONT_WIDTH) + IMAGE_WIDTH + MARGIN : 0
    maxAllowedLength -= 374 // 374 is the standardized width for the ccss grade + grade levels
  }
  return maxAllowedLength
}

const ActivityRowCheckbox = ({ activity, isSelected, toggleActivitySelection, }: ActivityRowCheckboxProps) => {
  const handleCheckboxClick = (e) => {
    e.stopPropagation()
    toggleActivitySelection(activity, isSelected)
  }

  return (
    <Checkbox
      ariaLabel={`Select ${safeStripHtml(activity.name)} for assignment`}
      onClick={handleCheckboxClick}
      selected={isSelected}
    />
  )
}

const ActivityRowConcept = ({ conceptName, focusCategoryName, }: { conceptName?: string, focusCategoryName?: string }) => {
  const className = "attribute-section concept"
  if (conceptName) {
    return (
      <span className={className}>
        <img alt="" src={conceptSrc} />
        <span className="sr-only">Activity Focus: </span>
        {focusCategoryName && <span className="focus-category-prefix">{focusCategoryName}: </span>}
        <span>{conceptName}</span>
      </span>
    )
  }

  return <span className={className} />
}

const ActivityRowTopics = ({ allTopics, topics, maxAllowedLength, onTertiaryLine, inExpandedView, hasActivityFocus, }: { allTopics?: Topic[], topics?: Topic[], maxAllowedLength: number, onTertiaryLine: boolean, inExpandedView: boolean, hasActivityFocus: boolean, }) => {
  const className = "attribute-section topic"

  if (!(topics && topics.length)) { return <span /> }

  if (inExpandedView && !onTertiaryLine) { return <span /> }

  const topicString = stringifyLowerLevelTopics(topics)
  const widthOfTopicSectionInPixels = (topicString.length * AVERAGE_FONT_WIDTH) + (topics.length * IMAGE_WIDTH) + (topics.length * MARGIN)
  const widthExceedsAllottedSpaceOnSecondLine = widthOfTopicSectionInPixels >= maxAllowedLength

  function getParentTopic(topic: Topic) {
    return allTopics.find(t => t.id === topic.parent_id)
  }

  if (inExpandedView && onTertiaryLine) {
    return topics.map((topic, i) => {
      const secondLevelTopic = getParentTopic(topic)
      const thirdLevelTopic = getParentTopic(secondLevelTopic)
      const diagonalDivider = <span className="diagonal-divider">/</span>
      return (
        <span className="attribute-section extended-topic" key={topic.name}>
          <span className="sr-only">Topic: </span>
          <span>{thirdLevelTopic?.name}</span>
          {diagonalDivider}
          <span>{secondLevelTopic?.name}</span>
          {diagonalDivider}
          <span className="lowest-level">{topic.name}</span>
        </span>
      )
    })
  } else if ((widthExceedsAllottedSpaceOnSecondLine && onTertiaryLine) || (!widthExceedsAllottedSpaceOnSecondLine && !onTertiaryLine)) {
    return topics.map((topic, i) => {
      return (
        <span className={className} key={topic.name}>
          <span className="sr-only">Topic: </span>
          {!onTertiaryLine && hasActivityFocus && i === 0 && <span className="vertical-divider" />}
          <span>{topic.name}</span>
          {i !== (topics.length - 1) && <span className="vertical-divider" />}
        </span>
      )
    })
  }

  return <span />
}


const ActivityRowGradeRange = ({
  gradeBandNames,
  gradeBandRange,
}: {
  gradeBandNames?: string[],
  gradeBandRange?: string,
}) => {
  const assignedBands = gradeBandNames || [];

  if (!assignedBands.length) { return <span className="attribute-section grade-range" /> }

  const floorIndex = TARGET_GRADE_RANGE_BANDS.indexOf(floorBand(assignedBands));

  const gradeBands = TARGET_GRADE_RANGE_BANDS.map((band, index) => {
    let className = 'grade-level';
    if (assignedBands.includes(band)) { className += ' filtered'; }
    else if (index > floorIndex) { className += ' higher-grade'; }
    else { className += ' not-recommended'; }

    return (
      <span className={className} key={band}>
        {band}
      </span>
    );
  });

  // the visual layout of the grade band spans doesn't translate well to screenreaders, so we hide it in favor of a plain-text version
  return (
    <span className="attribute-section grade-range">
      <span className="sr-only">Target Grade Range: {gradeBandRange || assignedBands.join(', ')}</span>
      <span aria-hidden={true}>
        {gradeBands}
      </span>
    </span>
  );
};

const ActivityRowStandardLevel = ({ standardLevelName, }: { standardLevelName?: string }) => {
  const className = "attribute-section standard-level"
  if (standardLevelName) {
    return (
      <span className={className}>
        <img alt="" src={ccssSrc} />
        <span className="sr-only">Standard Level: </span>
        <span>{standardLevelName}</span>
      </span>
    )
  }

  return <span />
}

const ActivityRowDescriptionLines = ({ activity, lineClassName, }: { activity: Activity, lineClassName: string }) => {
  const { description, content_partners, } = activity

  const descriptionLine = description && (
    <div className={lineClassName}>
      <h3 className="line-header">Activity Description</h3>
      <span>{safeParseHtml(description)}</span>
    </div>
  )

  const contentPartnerLine = content_partners?.length ? (
    <div className={lineClassName}>
      <h3 className="line-header">{content_partners.length > 1 ? 'Curriculum Partners' : 'Curriculum Partner'}</h3>
      {content_partners.map(cp => <span key={cp.id}>{cp.description}</span>)}
    </div>
  ) : null

  return (
    <React.Fragment>
      {descriptionLine}
      {contentPartnerLine}
    </React.Fragment>
  )
}

const ActivityRowExpandedSection = ({ activity, isExpanded }: { activity: Activity, isExpanded: boolean }) => {
  if (!isExpanded) { return }

  return (
    <div className="expanded-section" id={`${activity.id}ExpandedSection`}>
      <ActivityRowDescriptionLines activity={activity} lineClassName="expanded-line" />
    </div>
  )
}

const ActivityRowTooltip = ({ activity, tooltipRef, }: { activity: Activity, tooltipRef: React.RefObject<HTMLDivElement> }) => {
  return (
    <div aria-hidden={true} className="activity-row-tooltip-wrapper">
      <div className="activity-row-tooltip" ref={tooltipRef}>
        <ActivityRowDescriptionLines activity={activity} lineClassName="tooltip-line" />
      </div>
    </div>
  )
}

const ActivityRow = ({ activity, isSelected, toggleActivitySelection, showCheckbox = true, showRemoveButton, isFirst, setShowSnackbar, saveActivity, allTopics, unsaveActivity, savedActivityIds, }: ActivityRowProps) => {
  const size = useWindowSize();
  const [isExpanded, setIsExpanded] = React.useState(false)
  const [isHovered, setIsHovered] = React.useState(false)
  const [tooltipDismissed, setTooltipDismissed] = React.useState(false)
  const [tooltipAbove, setTooltipAbove] = React.useState(false)

  const rowRef = React.useRef<HTMLElement>(null)
  const tooltipRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    if (!isHovered) { return }

    function handleKeyDown(e) {
      if (e.key === ESCAPE) { setTooltipDismissed(true) }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isHovered])

  function toggleIsExpanded(e) {
    if (e.target.tagName !== 'a') {
      setIsExpanded(!isExpanded)
    }
  }

  function positionTooltip() {
    if (!(rowRef.current && tooltipRef.current)) { return }

    const rowRect = rowRef.current.getBoundingClientRect()
    const tooltipHeight = tooltipRef.current.offsetHeight
    const overflowsBottom = rowRect.bottom + tooltipHeight > window.innerHeight
    const fitsAbove = rowRect.top - tooltipHeight > 0

    setTooltipAbove(overflowsBottom && fitsAbove)
  }

  function handleMouseEnter() {
    setIsHovered(true)
    setTooltipDismissed(false)
    positionTooltip()
  }

  function handleMouseLeave() { setIsHovered(false) }

  const { activity_classification, name, activity_focus_name, activity_focus_category_name, standard_level_name, anonymous_path, grade_band_range, grade_band_names, topics, id, description, content_partners, } = activity

  function handleClickSaveButton(e) {
    e.stopPropagation()
    saveActivity(id)
  }
  function handleClickSavedButton(e) {
    e.stopPropagation()
    unsaveActivity(id)
  }

  function removeActivity(e) {
    e.stopPropagation()
    toggleActivitySelection(activity, isSelected)
    setShowSnackbar && setShowSnackbar(true)
  }

  const expandButtonAriaLabel = isExpanded ? 'Collapse to show less information' : 'Expand to show more information'

  const saveButton = <button className="interactive-wrapper focus-on-light save-button" onClick={handleClickSaveButton} type="button"><img alt="" src={outlinedBookmarkSrc} />Save</button>
  const savedButton = <button className="interactive-wrapper focus-on-light saved-button" onClick={handleClickSavedButton} type="button"><img alt="" src={bookmarkSrc} />Saved</button>
  const previewButton = <a className="interactive-wrapper focus-on-light preview-link" href={anonymous_path} rel="noopener noreferrer" target="_blank"><img alt="" src={previewSrc} />Preview</a>
  const expandButton = <button aria-controls={`${activity.id}ExpandedSection`} aria-expanded={isExpanded} aria-label={expandButtonAriaLabel} className="interactive-wrapper focus-on-light expand-button" onClick={toggleIsExpanded} type="button"><img alt="" src={expandSrc} /></button>
  const removeButton = <button className="interactive-wrapper focus-on-light remove-button" onClick={removeActivity} type="button"><img alt="" src={removeSrc} />Remove</button>
  const removeOrPreviewButton = showRemoveButton ? removeButton : previewButton
  const saveOrSavedButton = savedActivityIds && savedActivityIds.includes(id) ? savedButton : saveButton
  const nonToggleButtons = <div className="non-toggle-buttons">{removeOrPreviewButton}{saveOrSavedButton}</div>

  const expandClassName = isExpanded ? 'expanded' : 'not-expanded'
  const isSelectedClassName = isSelected ? 'selected' : 'not-selected'
  const isFirstClassName = isFirst ? 'is-first' : ''
  const tooltipDismissedClassName = tooltipDismissed ? 'tooltip-dismissed' : ''
  const tooltipAboveClassName = tooltipAbove ? 'tooltip-above' : ''

  const noContentForExpandedSection = !description && !content_partners?.length
  const expandedButEmptyClassName = isExpanded && noContentForExpandedSection ? 'no-expanded-content' : ''

  let topicLine

  if (topics && topics.length) {
    topicLine = (
      <div className="third-line">
        <ActivityRowTopics
          allTopics={allTopics}
          hasActivityFocus={!!activity_focus_name}
          inExpandedView={isExpanded}
          maxAllowedLength={calculateMaxAllowedLengthForTopicSection({ activity_classification, })}
          onTertiaryLine={true}
          topics={topics}
        />
      </div>
    )
  }

  return (
    // disabling jsx-a11y rules for section onclick because the toggle interaction already exists as its own button for keyboard users
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
    <section aria-label={safeStripHtml(name)} className={`activity-row ${expandClassName} ${isSelectedClassName} ${isFirstClassName} ${expandedButEmptyClassName} ${tooltipDismissedClassName} ${tooltipAboveClassName}`} onClick={toggleIsExpanded} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} ref={rowRef}>
      <div className="first-line">
        <div className="name-and-checkbox-wrapper">
          {showCheckbox && <ActivityRowCheckbox activity={activity} isSelected={isSelected} toggleActivitySelection={toggleActivitySelection} />}
          <h2>
            <ImageTagForClassification classificationKey={activity_classification.key} />
            <a className="focus-on-light" href={anonymous_path} rel="noopener noreferrer" target="_blank">{safeParseHtml(name)}</a>
          </h2>
        </div>
        <div className="buttons-wrapper">
          {nonToggleButtons}
          {expandButton}
        </div>
      </div>
      <div className="second-line">
        <div className="classification-concept-topic-wrapper">
          <ActivityRowConcept conceptName={activity_focus_name} focusCategoryName={activity_focus_category_name} />
          <ActivityRowTopics
            allTopics={allTopics}
            hasActivityFocus={!!activity_focus_name}
            inExpandedView={isExpanded}
            maxAllowedLength={calculateMaxAllowedLengthForTopicSection({ activity_classification, })}
            onTertiaryLine={false}
            topics={topics}
          />
        </div>
        <div className="grade-range-and-standard-level-wrapper">
          <ActivityRowGradeRange gradeBandNames={grade_band_names} gradeBandRange={grade_band_range} />
        </div>
      </div>
      <div className="third-line mobile-only">{nonToggleButtons}</div>
      {topicLine}
      {!noContentForExpandedSection && <ActivityRowTooltip activity={activity} tooltipRef={tooltipRef} />}
      <ActivityRowExpandedSection activity={activity} isExpanded={isExpanded} />
    </section>
  )
}

export default ActivityRow
